import React, { useState } from 'react'
import { Box, Card, Chip, Button, Slider } from '@mui/material'
import FlagIcon from '@mui/icons-material/Flag'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import TrackChangesIcon from '@mui/icons-material/TrackChanges'
import TuneIcon from '@mui/icons-material/Tune'
import { useTranslation } from 'react-i18next'
import WTypography from '../../../Components/whiteTypography'
import PlanObjectifPatrimoine from './PlanObjectifPatrimoine'
import { computeAccumulationPlan } from '../utils/buildAdvisorFromReports'

/**
 * PlanObjectifs (LAP-CP08)
 * ------------------------
 * SECTION REGROUPÉE qui affiche, en une seule carte de section, le plan
 * d'atterrissage pour CHAQUE objectif défini dans « Mes Objectifs » :
 *   - wealth         → délégué à PlanObjectifPatrimoine (carte patrimoine),
 *   - income / cashflow → écart récurrent + leviers ET équivalent capital,
 *   - passive_income → capital nécessaire (taux de distribution PARAMÉTRABLE),
 *   - yield          → rendement visé vs profil de risque (profil suggéré).
 *
 * Toute la logique financière vient du moteur (buildObjectivePlans) ; pour le
 * revenu passif, le curseur de taux de distribution recalcule en direct via
 * computeAccumulationPlan exporté par le moteur. La devise est formatée via
 * formatMoney (profil pays).
 */
function PlanObjectifs({ plans, formatMoney, onCreateProject, onAdaptObjective }) {
  const { t } = useTranslation('layout')
  if (!plans || plans.length === 0) return null
  const money = formatMoney || ((n) => `${n}`)

  // Les cibles de budget (dépense/épargne mensuelles) sont regroupées dans une
  // section dédiée, à part des plans d'objectifs (patrimoine, revenu, etc.).
  const budgetPlans = plans.filter((p) => p.kind === 'budget')
  const objectivePlans = plans.filter((p) => p.kind !== 'budget')

  return (
    <Card
      sx={{
        background: '#0e1a36',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 3,
        p: 3,
        color: 'white',
      }}
    >
      <Box display="flex" alignItems="center" gap={1.2} mb={0.5}>
        <TrackChangesIcon sx={{ color: '#60a5fa' }} />
        <WTypography
          variant="overline"
          sx={{ color: '#a0b4d6 !important', letterSpacing: 1.5 }}
        >
          {t('conseillerPatrimonial.planObjectifs.sectionOverline')}
        </WTypography>
      </Box>
      <WTypography variant="h5" sx={{ fontWeight: 700 }}>
        {t('conseillerPatrimonial.planObjectifs.sectionTitle')}
      </WTypography>
      <WTypography variant="body2" sx={{ color: '#c1d0eb !important', mt: 0.5, mb: 1 }}>
        {t('conseillerPatrimonial.planObjectifs.sectionSubtitle', {
          count: plans.length,
        })}
      </WTypography>

      <Box display="flex" flexDirection="column" gap={3} mt={2}>
        {objectivePlans.map((plan, i) => (
          <PlanRouter
            key={plan.id != null ? plan.id : `obj-${i}`}
            plan={plan}
            money={money}
            t={t}
            onCreateProject={onCreateProject}
            onAdaptObjective={onAdaptObjective}
          />
        ))}
        {budgetPlans.length > 0 && (
          <BudgetSection plans={budgetPlans} money={money} t={t} />
        )}
      </Box>
    </Card>
  )
}

// Section BUDGET regroupée : cibles de dépense/épargne mensuelles, exprimées
// EN DEVISE, sans aucune conversion en capital ni en rendement.
function BudgetSection({ plans, money, t }) {
  return (
    <SubCard accent="#fbbf24">
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        <Chip
          label={t('conseillerPatrimonial.planObjectifs.kind.budget')}
          size="small"
          sx={{
            bgcolor: 'rgba(251,191,36,0.15)',
            color: '#fcd34d',
            fontWeight: 700,
            fontSize: 11,
            height: 22,
          }}
        />
      </Box>
      <WTypography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {t('conseillerPatrimonial.planObjectifs.budget.title')}
      </WTypography>
      <WTypography variant="caption" sx={{ color: '#8da3c9 !important' }}>
        {t('conseillerPatrimonial.planObjectifs.budget.note')}
      </WTypography>

      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
        gap={1.5}
        mt={2}
      >
        {plans.map((p, i) => {
          const periodicity = t(
            `conseillerPatrimonial.planObjectifs.periodicity.${p.periodicityKey}`,
          )
          // pt3 — Si l'objectif est stocké en %, on affiche la valeur en devise
          // (calculée sur le revenu de référence) en valeur principale, et on
          // rappelle le pourcentage en légende. Sinon, on affiche la devise brute.
          const mainValue = money(
            p.targetCurrency != null ? p.targetCurrency : p.targetPeriodic,
          )
          const showPct = p.isPercent && p.referenceIncome > 0
          return (
            <Box
              key={p.id != null ? p.id : `bud-${i}`}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <Box>
                <WTypography variant="body2" sx={{ fontWeight: 600 }}>
                  {p.objectiveName || '—'}
                </WTypography>
                <WTypography variant="caption" sx={{ color: '#8da3c9 !important' }}>
                  {p.isSaving
                    ? t('conseillerPatrimonial.planObjectifs.budget.savingTag')
                    : t('conseillerPatrimonial.planObjectifs.budget.expenseTag')}
                  {' · '}
                  {periodicity}
                  {showPct
                    ? ` · ${t('conseillerPatrimonial.planObjectifs.budget.pctOfIncome', {
                        pct: p.targetPct,
                      })}`
                    : ''}
                </WTypography>
              </Box>
              <WTypography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color: `${p.isSaving ? '#4ade80' : '#fcd34d'} !important`,
                  whiteSpace: 'nowrap',
                  ml: 1,
                }}
              >
                {mainValue}
              </WTypography>
            </Box>
          )
        })}
      </Box>
    </SubCard>
  )
}

// Aiguille vers le bon sous-rendu selon le type d'objectif.
function PlanRouter({ plan, money, t, onCreateProject, onAdaptObjective }) {
  const onAdapt = onAdaptObjective ? () => onAdaptObjective(plan) : null
  if (plan.kind === 'wealth') {
    // La carte patrimoine est déjà polie ; on construit le projet « capital ».
    return (
      <PlanObjectifPatrimoine
        plan={plan}
        formatMoney={money}
        onCreateProject={(p) => onCreateProject(buildWealthProjet(p, t, money))}
        onAdaptObjective={onAdapt}
      />
    )
  }
  if (plan.kind === 'yield') {
    return <YieldCard plan={plan} t={t} onCreateProject={onCreateProject} onAdapt={onAdapt} />
  }
  if (plan.kind === 'passive_income') {
    return (
      <PassiveIncomeCard
        plan={plan}
        money={money}
        t={t}
        onCreateProject={onCreateProject}
        onAdapt={onAdapt}
      />
    )
  }
  // income | cashflow
  return (
    <FlowCard
      plan={plan}
      money={money}
      t={t}
      onCreateProject={onCreateProject}
      onAdapt={onAdapt}
    />
  )
}

// ───────────────────────── Cartes par type ─────────────────────────

function FlowCard({ plan, money, t, onCreateProject, onAdapt }) {
  const hasGap = plan.gapMonthly > 0
  const periodicity = t(`conseillerPatrimonial.planObjectifs.periodicity.${plan.periodicityKey}`)
  const kindLabel = t(`conseillerPatrimonial.planObjectifs.kind.${plan.kind}`)
  const acc = plan.capitalPlan

  return (
    <SubCard accent="#34d399">
      <CardHead
        kindLabel={kindLabel}
        name={plan.objectiveName}
        verdict={plan.verdict}
        horizon={plan.horizonYears}
        date={plan.targetDateLabel}
        t={t}
      />

      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }}
        gap={2}
        mt={2}
      >
        <KPI
          label={t('conseillerPatrimonial.planObjectifs.flow.targetLabel', { periodicity })}
          value={money(plan.targetPeriodic)}
          color="#dbe5f7"
        />
        <KPI
          label={t('conseillerPatrimonial.planObjectifs.flow.currentLabel')}
          value={money(plan.currentMonthly)}
          color="#dbe5f7"
        />
        <KPI
          label={t('conseillerPatrimonial.planObjectifs.flow.gapLabel')}
          value={money(Math.abs(plan.gapMonthly))}
          color={hasGap ? '#f87171' : '#4ade80'}
          highlight
        />
      </Box>

      {hasGap ? (
        <InfoBlock accent="#34d399">
          <WTypography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {t('conseillerPatrimonial.planObjectifs.flow.leversTitle')}
          </WTypography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, color: '#c1d0eb' }}>
            <Lever
              text={t('conseillerPatrimonial.planObjectifs.flow.leverGap', {
                amount: money(plan.gapMonthly),
                target: money(plan.targetMonthly),
              })}
            />
            <Lever
              text={t('conseillerPatrimonial.planObjectifs.flow.leverCapital', {
                capital: money(plan.capitalEquivalent),
                rate: plan.distributionRatePct,
              })}
            />
            <Lever
              text={t('conseillerPatrimonial.planObjectifs.flow.leverSave', {
                amount: money(acc.requiredMonthlySavings),
                years: plan.horizonYears,
              })}
            />
          </Box>
        </InfoBlock>
      ) : (
        <InfoBlock accent="#22c55e" success>
          <WTypography variant="body2" sx={{ color: '#c1d0eb !important' }}>
            {t('conseillerPatrimonial.planObjectifs.flow.onTrack', {
              target: money(plan.targetMonthly),
            })}
          </WTypography>
        </InfoBlock>
      )}

      {onCreateProject && (
        <CreateProjectButton
          t={t}
          onAdapt={onAdapt}
          onClick={() =>
            onCreateProject(buildCapitalProjet(plan, plan.capitalEquivalent, acc, t, money))
          }
        />
      )}
    </SubCard>
  )
}

function PassiveIncomeCard({ plan, money, t, onCreateProject, onAdapt }) {
  // Curseur de taux de distribution PARAMÉTRABLE → recalcul live du capital
  // nécessaire et du plan d'accumulation associé.
  const [distPct, setDistPct] = useState(plan.distributionRatePct)
  const rate = (plan.expectedReturnPct || 0) / 100
  const requiredCapital = Math.round(plan.targetAnnual / (distPct / 100))
  const acc = computeAccumulationPlan(
    plan.capitalPlan.currentNetWorth,
    requiredCapital,
    rate,
    plan.horizonYears,
    plan.capitalPlan.currentMonthlySavings,
  )
  const kindLabel = t(`conseillerPatrimonial.planObjectifs.kind.${plan.kind}`)
  const periodicity = t(`conseillerPatrimonial.planObjectifs.periodicity.${plan.periodicityKey}`)

  return (
    <SubCard accent="#a78bfa">
      <CardHead
        kindLabel={kindLabel}
        name={plan.objectiveName}
        verdict={acc.verdict}
        horizon={plan.horizonYears}
        date={plan.targetDateLabel}
        t={t}
      />

      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }}
        gap={2}
        mt={2}
      >
        <KPI
          label={t('conseillerPatrimonial.planObjectifs.passive.targetLabel', { periodicity })}
          value={money(plan.targetPeriodic)}
          color="#dbe5f7"
        />
        <KPI
          label={t('conseillerPatrimonial.planObjectifs.passive.capitalLabel')}
          value={money(requiredCapital)}
          color="#c4b5fd"
          highlight
        />
        <KPI
          label={t('conseillerPatrimonial.planObjectifs.passive.requiredMonthly')}
          value={money(acc.requiredMonthlySavings)}
          color={acc.verdict === 'reachable' ? '#4ade80' : '#fbbf24'}
          highlight
        />
      </Box>

      <Box sx={{ mt: 2.5 }}>
        <WTypography variant="caption" sx={{ color: '#a0b4d6 !important' }}>
          {t('conseillerPatrimonial.planObjectifs.passive.distributionLabel', {
            rate: distPct,
          })}
        </WTypography>
        <Slider
          value={distPct}
          onChange={(e, v) => setDistPct(v)}
          min={2}
          max={8}
          step={0.5}
          marks
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v}%`}
          sx={{
            color: '#a78bfa',
            mt: 0.5,
            '& .MuiSlider-markLabel': { color: '#8da3c9' },
          }}
        />
        <WTypography variant="caption" sx={{ color: '#8da3c9 !important' }}>
          {t('conseillerPatrimonial.planObjectifs.passive.distributionHint')}
        </WTypography>
      </Box>

      <InfoBlock accent="#a78bfa">
        <WTypography variant="body2" sx={{ color: '#c1d0eb !important' }}>
          {t('conseillerPatrimonial.planObjectifs.passive.body', {
            capital: money(requiredCapital),
            income: money(plan.targetMonthly),
            rate: distPct,
            years: plan.horizonYears,
          })}
        </WTypography>
      </InfoBlock>

      {onCreateProject && (
        <CreateProjectButton
          t={t}
          onAdapt={onAdapt}
          onClick={() =>
            onCreateProject(buildCapitalProjet(plan, requiredCapital, acc, t, money))
          }
        />
      )}
    </SubCard>
  )
}

function YieldCard({ plan, t, onCreateProject, onAdapt }) {
  const hasGap = plan.gapPct > 0
  const kindLabel = t(`conseillerPatrimonial.planObjectifs.kind.${plan.kind}`)
  const suggestedProfile = t(
    `conseillerPatrimonial.planObjectif.risk.${plan.suggestedProfileKey}`,
  )

  return (
    <SubCard accent="#38bdf8">
      <CardHead
        kindLabel={kindLabel}
        name={plan.objectiveName}
        verdict={plan.verdict}
        horizon={plan.horizonYears}
        date={plan.targetDateLabel}
        t={t}
      />

      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }}
        gap={2}
        mt={2}
      >
        <KPI
          label={t('conseillerPatrimonial.planObjectifs.yield.currentLabel')}
          value={`${plan.currentRatePct}%`}
          color="#dbe5f7"
        />
        <KPI
          label={t('conseillerPatrimonial.planObjectifs.yield.targetLabel')}
          value={`${plan.targetRatePct}%`}
          color="#7dd3fc"
          highlight
        />
        <KPI
          label={t('conseillerPatrimonial.planObjectifs.yield.gapLabel')}
          value={`${hasGap ? '+' : ''}${plan.gapPct}%`}
          color={hasGap ? '#fbbf24' : '#4ade80'}
          highlight
        />
      </Box>

      {plan.verdict === 'unreachable' ? (
        <InfoBlock accent="#f87171">
          <WTypography variant="body2" sx={{ color: '#c1d0eb !important' }}>
            {t('conseillerPatrimonial.planObjectifs.yield.unreachableBody', {
              target: plan.targetRatePct,
              max: plan.maxProfileReturnPct,
            })}
          </WTypography>
        </InfoBlock>
      ) : hasGap ? (
        <InfoBlock accent="#38bdf8">
          <WTypography variant="body2" sx={{ color: '#c1d0eb !important' }}>
            {t('conseillerPatrimonial.planObjectifs.yield.suggestBody', {
              target: plan.targetRatePct,
              profile: suggestedProfile,
            })}
          </WTypography>
        </InfoBlock>
      ) : (
        <InfoBlock accent="#22c55e" success>
          <WTypography variant="body2" sx={{ color: '#c1d0eb !important' }}>
            {t('conseillerPatrimonial.planObjectifs.yield.reachableBody', {
              target: plan.targetRatePct,
              current: plan.currentRatePct,
            })}
          </WTypography>
        </InfoBlock>
      )}

      {onCreateProject && (
        <CreateProjectButton
          t={t}
          onAdapt={onAdapt}
          onClick={() => onCreateProject(buildYieldProjet(plan, suggestedProfile, t))}
        />
      )}
    </SubCard>
  )
}

// ───────────────────────── Atomes partagés ─────────────────────────

function SubCard({ accent, children }) {
  return (
    <Box
      sx={{
        borderRadius: 2.5,
        p: 2.5,
        bgcolor: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.07)',
        borderLeft: `3px solid ${accent}`,
      }}
    >
      {children}
    </Box>
  )
}

const VERDICT_MAP = {
  reachable: { color: '#4ade80', bg: 'rgba(34,197,94,0.18)', icon: CheckCircleIcon },
  tight: { color: '#fbbf24', bg: 'rgba(251,191,36,0.18)', icon: WarningAmberIcon },
  hard: { color: '#fb923c', bg: 'rgba(251,146,60,0.18)', icon: WarningAmberIcon },
  unreachable: { color: '#f87171', bg: 'rgba(248,113,113,0.18)', icon: ErrorOutlineIcon },
}

function CardHead({ kindLabel, name, verdict, horizon, date, t }) {
  const vd = VERDICT_MAP[verdict] || VERDICT_MAP.tight
  const VIcon = vd.icon
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="flex-start"
      flexWrap="wrap"
      gap={1.5}
    >
      <Box>
        <Chip
          label={kindLabel}
          size="small"
          sx={{
            bgcolor: 'rgba(96,165,250,0.15)',
            color: '#93c5fd',
            fontWeight: 700,
            fontSize: 11,
            height: 22,
            mb: 0.5,
          }}
        />
        <WTypography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {name || kindLabel}
        </WTypography>
        <WTypography variant="caption" sx={{ color: '#8da3c9 !important' }}>
          {t('conseillerPatrimonial.planObjectifs.horizonLabel', {
            years: horizon,
            date: date || '—',
          })}
        </WTypography>
      </Box>
      <Chip
        icon={<VIcon sx={{ color: 'inherit !important' }} />}
        label={t(`conseillerPatrimonial.planObjectif.verdict.${verdict}`)}
        sx={{
          bgcolor: vd.bg,
          color: vd.color,
          fontWeight: 700,
          fontSize: 12,
          height: 30,
          px: 1,
        }}
      />
    </Box>
  )
}

function InfoBlock({ accent, success, children }) {
  return (
    <Box
      sx={{
        mt: 2.5,
        p: 2,
        borderRadius: 2,
        bgcolor: success ? 'rgba(34,197,94,0.06)' : `${accent}10`,
        border: `1px solid ${accent}33`,
      }}
    >
      {children}
    </Box>
  )
}

function CreateProjectButton({ t, label, onClick, onAdapt }) {
  return (
    <Box mt={2.5} display="flex" justifyContent="flex-end" gap={1.5} flexWrap="wrap">
      {onAdapt && (
        <Button
          variant="outlined"
          startIcon={<TuneIcon />}
          onClick={onAdapt}
          sx={{
            color: '#dbe5f7',
            borderColor: 'rgba(255,255,255,0.2)',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': { borderColor: '#dbe5f7', bgcolor: 'rgba(255,255,255,0.04)' },
          }}
        >
          {t('conseillerPatrimonial.planObjectif.ctaAdaptObjective')}
        </Button>
      )}
      <Button
        variant="contained"
        startIcon={<FlagIcon />}
        onClick={onClick}
        sx={{
          bgcolor: '#2563eb',
          '&:hover': { bgcolor: '#1d4ed8' },
          fontWeight: 700,
          textTransform: 'none',
        }}
      >
        {label || t('conseillerPatrimonial.planObjectif.ctaCreateProject')}
      </Button>
    </Box>
  )
}

function Lever({ text }) {
  return (
    <li style={{ marginBottom: 6 }}>
      <WTypography variant="body2" component="span" sx={{ color: '#c1d0eb !important' }}>
        {text}
      </WTypography>
    </li>
  )
}

function KPI({ label, value, color, highlight }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: highlight ? `${color}15` : 'rgba(255,255,255,0.03)',
        border: `1px solid ${highlight ? color + '40' : 'rgba(255,255,255,0.06)'}`,
      }}
    >
      <WTypography variant="caption" sx={{ color: '#a0b4d6 !important' }}>
        {label}
      </WTypography>
      <WTypography variant="h6" sx={{ fontWeight: 700, color: `${color} !important`, mt: 0.5 }}>
        {value}
      </WTypography>
    </Box>
  )
}

// ──────────────────── Construction des projets d'épargne ────────────────────
// Chaque CTA produit un objet « projet » prêt pour navigateToCreateProject :
//   { title, description, autofilled: { duration_months, target_amount,
//     monthly_contribution } }.

function buildWealthProjet(plan, t, money) {
  const title = t('conseillerPatrimonial.planObjectif.projectTitle', {
    amount: money(plan.targetAmount),
  })
  const description = t('conseillerPatrimonial.planObjectif.projectDescription', {
    amount: money(plan.targetAmount),
    years: plan.horizonYears,
    monthly: money(plan.requiredMonthlySavings),
  })
  return {
    title,
    description,
    autofilled: {
      duration_months: plan.horizonYears * 12,
      target_amount: plan.targetAmount,
      monthly_contribution: plan.requiredMonthlySavings,
    },
  }
}

function buildCapitalProjet(plan, capital, acc, t, money) {
  const title = t('conseillerPatrimonial.planObjectifs.project.capitalTitle', {
    amount: money(capital),
  })
  const description = t('conseillerPatrimonial.planObjectifs.project.capitalDescription', {
    amount: money(capital),
    years: plan.horizonYears,
    monthly: money(acc.requiredMonthlySavings),
  })
  return {
    title,
    description,
    autofilled: {
      duration_months: plan.horizonYears * 12,
      target_amount: capital,
      monthly_contribution: acc.requiredMonthlySavings,
    },
  }
}

function buildYieldProjet(plan, suggestedProfile, t) {
  const title = t('conseillerPatrimonial.planObjectifs.project.yieldTitle', {
    rate: plan.targetRatePct,
  })
  const description = t('conseillerPatrimonial.planObjectifs.project.yieldDescription', {
    rate: plan.targetRatePct,
    profile: suggestedProfile,
  })
  return {
    title,
    description,
    autofilled: {
      duration_months: plan.horizonYears * 12,
      target_rate: plan.targetRatePct,
      suggested_profile: plan.suggestedProfileKey,
    },
  }
}

export default PlanObjectifs
